import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
	InterviewRecording,
	InterviewTranscript,
	PresentationDraft,
	PresentationDraftSource,
	StartPresentationDraftRequest
} from "../models/PresentationDraft";

export const getPresentationDrafts = async (freelancerProfileId: number): Promise<PresentationDraft[]> => {
	try {
		const response = await apiClient.get(`/presentation-drafts?freelancerProfileId=${freelancerProfileId}`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to fetch presentation drafts");
	}
};

export const startPresentationDraft = async (request: StartPresentationDraftRequest): Promise<PresentationDraft> => {
	try {
		const response = await apiClient.post("/presentation-drafts", request);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to start the presentation draft");
	}
};

export const getPresentationDraftSource = async (draftId: number): Promise<PresentationDraftSource> => {
	try {
		const response = await apiClient.get(`/presentation-drafts/${draftId}/source`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to fetch the presentation draft source");
	}
};

export const updatePresentationDraftContent = async (draftId: number, content: string): Promise<PresentationDraft> => {
	try {
		const response = await apiClient.put(`/presentation-drafts/${draftId}/content`, { content });
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to save the presentation draft");
	}
};

export const regeneratePresentationDraft = async (draftId: number, instruction: string): Promise<PresentationDraft> => {
	try {
		const response = await apiClient.post(`/presentation-drafts/${draftId}/regenerate`, { instruction });
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to regenerate the presentation draft");
	}
};

export const retryPresentationDraft = async (draftId: number): Promise<PresentationDraft> => {
	try {
		const response = await apiClient.post(`/presentation-drafts/${draftId}/retry`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to retry the presentation draft");
	}
};

export const publishPresentationDraft = async (draftId: number, isExemplary: boolean): Promise<PresentationDraft> => {
	try {
		const response = await apiClient.post(`/presentation-drafts/${draftId}/publish`, { isExemplary });
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to publish the presentation draft");
	}
};

export const discardPresentationDraft = async (draftId: number): Promise<PresentationDraft> => {
	try {
		const response = await apiClient.post(`/presentation-drafts/${draftId}/discard`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to discard the presentation draft");
	}
};

export const getInterviewRecordings = async (freelancerProfileId: number): Promise<InterviewRecording[]> => {
	try {
		const response = await apiClient.get(`/presentation-drafts/interviews?freelancerProfileId=${freelancerProfileId}`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to fetch the interview recordings");
	}
};

export const importInterviewTranscript = async (
	freelancerProfileId: number,
	recordingId: string
): Promise<InterviewTranscript> => {
	try {
		const response = await apiClient.post(
			`/presentation-drafts/interviews/${encodeURIComponent(recordingId)}/import?freelancerProfileId=${freelancerProfileId}`
		);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to import the interview transcript");
	}
};
